/**
 * Response Transpiler
 *
 * Transpiles ElasticSearch response to normalized format matching Algolia response structure.
 * This ensures components don't need changes when switching between backends.
 *
 * Reference: fe-search-redux/src/backend/elastic/transpileElasticSearchResponse.ts
 */

/**
 * Transpile highlight result from ES format to normalized format
 * @param {Object} highlight - ES highlight object
 * @returns {Object} Normalized highlight result
 */
const transpileHighlightResult = (highlight) => {
  if (!highlight) {
    return {}
  }

  const result = {}

  Object.keys(highlight).forEach((attribute) => {
    result[attribute] = highlight[attribute].map((highlightedValue) => ({
      value: highlightedValue,
      // Remove <em> tags to get clean attribute value
      attributeValue: highlightedValue.replace(/<\/?em>/g, ""),
    }))
  })

  return result
}

/**
 * Transpile hits from ES response
 * @param {Array} hits - ES hits array
 * @returns {Array} Transpiled hits with _source flattened
 */
const transpileHits = (hits) => {
  if (!hits || hits.length === 0) {
    return []
  }

  return hits.map(({ _source, highlight, _score }) => {
    const hit = { ..._source }

    if (highlight) {
      hit._highlightResult = transpileHighlightResult(highlight)
    }

    if (_score) {
      hit._score = _score
    }

    return hit
  })
}

/**
 * Transpile facets/aggregations from ES response
 * @param {Object} aggregations - ES aggregations object
 * @returns {Object} Normalized facets object
 */
const transpileFacets = (aggregations) => {
  if (!aggregations) {
    return {}
  }

  const facets = {}

  Object.keys(aggregations).forEach((facetName) => {
    let facet = aggregations[facetName]

    // Handle filtered/global aggregations
    if (facet.filtered_agg) {
      facet = facet.filtered_agg.facet
    }

    if (facet.buckets) {
      facets[facetName] = facet.buckets.reduce((acc, bucket) => {
        // Use bucket.key as both key and value for simple facets
        let value = bucket.key

        // For complex facets with top_hits, extract the value
        if (bucket.complex_value?.hits?.hits?.length > 0) {
          const sourceValue = bucket.complex_value.hits.hits[0]._source[facetName]
          if (sourceValue) {
            value = sourceValue
          }
        }

        acc[value] = bucket.doc_count
        return acc
      }, {})
    }
  })

  return facets
}

/**
 * Main function to transpile ElasticSearch response to normalized format
 *
 * @param {Object} elasticResponse - Raw ES response
 * @param {Object} requestOptions - Original request options for context
 * @param {number} [requestOptions.page=1] - Page number
 * @param {number} [requestOptions.hitsPerPage=25] - Results per page
 * @param {string} [requestOptions.indexName] - Index name
 * @returns {Object} Normalized response matching Algolia format
 */
export const transpileSearchResponse = (elasticResponse, requestOptions = {}) => {
  const { page = 1, hitsPerPage = 25, indexName = "" } = requestOptions

  const hits = transpileHits(elasticResponse.hits?.hits)
  const totalHits = elasticResponse.hits?.total?.value || hits.length
  const hitRelation = elasticResponse.hits?.total?.relation || "gte"

  // CAP: Limit ads to maximum results (configurable via env)
  const MAX_ADS = parseInt(process.env.NEXT_PUBLIC_MAX_ADS_LIMIT, 10)
  const cappedTotalHits = Math.min(totalHits, MAX_ADS)

  return {
    // Core hit data
    hits,
    originalNbHits: totalHits > MAX_ADS ? totalHits : undefined, // Preserve original count for display
    nbHits: cappedTotalHits,

    // Pagination info
    page: page - 1, // Algolia uses 0-indexed pages
    hitsPerPage,
    nbPages: Math.ceil(cappedTotalHits / hitsPerPage),

    // Metadata
    index: indexName,
    processingTimeMS: elasticResponse.took || 0,

    // Whether total count is exact or estimate
    exhaustiveNbHits: hitRelation === "eq",

    // Facets
    facets: transpileFacets(elasticResponse.aggregations),
  }
}

/**
 * Transpile a simple search response (for autocomplete, etc.)
 *
 * @param {Object} elasticResponse - Raw ES response
 * @returns {Object} Simplified response with just hits
 */
export const transpileSimpleSearchResponse = (elasticResponse) => {
  return {
    hits: transpileHits(elasticResponse.hits?.hits),
    nbHits: elasticResponse.hits?.total?.value || 0,
  }
}

/**
 * Transpile location search response
 * Similar to transpileSimpleSearchResponse but with location-specific handling
 *
 * @param {Object} elasticResponse - Raw ES response
 * @returns {Object} Response with location hits
 */
export const transpileLocationSearchResponse = (elasticResponse) => {
  const hits = transpileHits(elasticResponse.hits?.hits)

  return {
    hits,
    nbHits: elasticResponse.hits?.total?.value || hits.length,
  }
}

export default transpileSearchResponse
